import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { omit } from 'lodash';
import { useParams } from 'react-router';

import { eventTypeEnum, type IEventList, type IMedicalDeviceAdjustment } from '../types';

export default function useDeviceAdjustmentService() {
  const queryClient = useQueryClient();
  const { patientUuid = '' } = useParams<{ patientUuid: string }>();

  const { data, isLoading } = useQuery<{ result: IEventList[] }>({
    queryKey: ['camel', 'patient_profile', 'clinical_info', patientUuid, 'events_timeline'],
    enabled: !!patientUuid,
  });

  const addAction = useMutation({
    mutationFn: async (data: Partial<IMedicalDeviceAdjustment>) => {
      const result = await axios.post<IMedicalDeviceAdjustment>(
        `/api/camel/patient_profile/clinical_info/medical_device_adjustment`,
        data,
      );
      invalidateClinicalQuery();
      return result?.data?.uuid;
    },
  });

  const editAction = useMutation({
    mutationFn: async (data: Partial<IMedicalDeviceAdjustment>) => {
      await axios.put(
        `/api/camel/patient_profile/clinical_info/medical_device_adjustment/${data.uuid}`,
        {
          ...omit(data, ['uuid']),
        },
      );
      invalidateClinicalQuery();
    },
  });
  const deleteAction = useMutation({
    mutationFn: async (medicalDeviceUuid: string) => {
      await axios.delete(
        `/api/camel/patient_profile/clinical_info/medical_device_adjustment/${medicalDeviceUuid}`,
      );
      invalidateClinicalQuery();
    },
  });

  const invalidateClinicalQuery = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['camel', 'patient_profile', 'clinical_info', patientUuid],
    });
  };

  return {
    events: (data?.result ?? []) as IEventList[],
    deviceAdjustments: (data?.result ?? []).filter(
      (item) => item.eventType === eventTypeEnum.medical_device_adjustment,
    ) as IEventList[],
    isLoading: !!patientUuid && isLoading,
    addMedicalDevice: addAction.mutateAsync,
    editMedicalDevice: editAction.mutateAsync,
    deleteMedicalDevice: deleteAction.mutateAsync,
  };
}
